/**
 * CUSTOMER FIX Stage 7 — persisted multi-cart registry (localStorage).
 */

import {
  CART_REGISTRY_STORAGE_KEY,
  isCartRegistryRawCorrupt,
  readRawCartRegistry,
  type CartRegistryEntryV1,
  writeCartRegistry,
} from './cartRegistryStorage.js';
import { buildDefaultShopHref } from './cartShopHref.js';
import {
  localStorageHasAnyCartPayload,
  readPersistedCartQuantity,
} from './cartPersistenceRead.js';
import { migrateSessionCartsToLocalStorage } from './migrateSessionCarts.js';
import {
  resolveTenantCartColor,
  resolveTenantCartInitial,
} from './tenantCartColor.js';

export {
  CART_REGISTRY_STORAGE_KEY,
  CUSTOMER_CART_REGISTRY_CHANGED_EVENT,
  upsertCartRegistryEntry,
  removeCartRegistryEntry,
} from './cartRegistryStorage.js';
export type { CartRegistryEntryV1, UpsertCartRegistryEntryInput } from './cartRegistryStorage.js';

export interface CartRegistryRow extends CartRegistryEntryV1 {
  readonly tenantColor: string;
  readonly tenantInitial: string;
}

function enrichRow(entry: CartRegistryEntryV1): CartRegistryRow {
  return {
    ...entry,
    tenantColor: resolveTenantCartColor(entry.tenantCode),
    tenantInitial: resolveTenantCartInitial(entry.tenantName, entry.tenantCode),
  };
}

function rawNonemptyRegistryCount(): number {
  return readRawCartRegistry().entries.filter((entry) => entry.totalQuantity > 0).length;
}

/**
 * Rebuild registry from cart payloads when registry is empty but localStorage carts exist.
 * Safe before any registry read on /browse chrome (Stage 7 G1).
 */
export function ensureCartRegistryBootstrapped(): void {
  migrateSessionCartsToLocalStorage();
  if (rawNonemptyRegistryCount() > 0) {
    return;
  }
  const rawRegistry =
    typeof window !== 'undefined' && window.localStorage !== undefined
      ? window.localStorage.getItem(CART_REGISTRY_STORAGE_KEY)
      : null;
  if (rawRegistry !== null && !isCartRegistryRawCorrupt(rawRegistry)) {
    return;
  }
  if (!localStorageHasAnyCartPayload()) {
    return;
  }
  rebuildCartRegistryFromStorage(readPersistedCartQuantity);
}

export function listNonemptyCartRegistryRows(): readonly CartRegistryRow[] {
  ensureCartRegistryBootstrapped();
  const rows = readRawCartRegistry()
    .entries.map(enrichRow)
    .filter((row) => row.totalQuantity > 0);
  return rows.sort((a, b) => b.updatedAt - a.updatedAt);
}

export function countNonemptyTenantCarts(): number {
  return listNonemptyCartRegistryRows().length;
}

export function hasAnyNonemptyPersistedCart(): boolean {
  return countNonemptyTenantCarts() > 0;
}

function registryEntriesSemanticallyEqual(
  next: readonly CartRegistryEntryV1[],
  existing: readonly CartRegistryEntryV1[],
): boolean {
  if (next.length !== existing.length) {
    return false;
  }
  for (const entry of next) {
    const prior = existing.find(
      (row) =>
        row.tenantCode === entry.tenantCode &&
        row.kioskId === entry.kioskId &&
        row.totalQuantity === entry.totalQuantity &&
        row.tenantName === entry.tenantName &&
        row.salesPointLabel === entry.salesPointLabel &&
        row.shopHref === entry.shopHref,
    );
    if (prior === undefined) {
      return false;
    }
  }
  return true;
}

export function rebuildCartRegistryFromStorage(
  readQuantity: (tenantCode: string, kioskId: number) => number,
): void {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return;
  }
  const existingEntries = readRawCartRegistry().entries;
  const prefix = 'rpapp-customer.shop.cart:';
  const entries: CartRegistryEntryV1[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const storageKey = window.localStorage.key(i);
    if (storageKey === null || !storageKey.startsWith(prefix)) {
      continue;
    }
    if (storageKey === CART_REGISTRY_STORAGE_KEY) {
      continue;
    }
    const suffix = storageKey.slice(prefix.length);
    const colon = suffix.lastIndexOf(':');
    if (colon <= 0) {
      continue;
    }
    const tenantCode = suffix.slice(0, colon);
    const kioskId = Number.parseInt(suffix.slice(colon + 1), 10);
    if (!Number.isFinite(kioskId) || kioskId <= 0) {
      continue;
    }
    const totalQuantity = readQuantity(tenantCode, kioskId);
    if (totalQuantity <= 0) {
      continue;
    }
    const prior = existingEntries.find(
      (entry) => entry.tenantCode === tenantCode && entry.kioskId === kioskId,
    );
    entries.push({
      tenantCode,
      kioskId,
      tenantName: prior?.tenantName ?? tenantCode,
      salesPointLabel: prior?.salesPointLabel ?? String(kioskId),
      shopHref: prior?.shopHref ?? buildDefaultShopHref(tenantCode, kioskId),
      totalQuantity,
      updatedAt: prior?.updatedAt ?? Date.now(),
    });
  }
  if (registryEntriesSemanticallyEqual(entries, existingEntries)) {
    return;
  }
  writeCartRegistry(entries);
}
